// src/modulos/Cadastros/CarrierListReportPage.js

import React, { useState, useMemo, useEffect } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal,
    ConfirmModal,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    PageHeader,
    Toast
} from '../../components/common';
import styles from '../Registers/Registers.module.css';

// Collections
const CARRIERS_COLLECTION = 'transportadoras';
const REPORT_HISTORY_COLLECTION = 'carrier-report-history';
const ADDRESS_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-endereco'
};

const TIPO_PESSOA_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Jurídica', label: 'Pessoa Jurídica' },
    { value: 'Física', label: 'Pessoa Física' }
];

const SITUACAO_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Inativo', label: 'Inativo' }
];

const ITEMS_PER_PAGE = 10;

const CarrierListReportPage = () => {
    // Report state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        searchTerm: '',
        tipoPessoa: '',
        situacao: '',
        tipoEndereco: '',
        uf: '',
        cidade: '',
        minCargaMaxima: '',
        maxCargaMaxima: '',
        dateFrom: '',
        dateTo: ''
    });

    // Pagination for main list
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch carriers
    const { data: carriers, loading: carriersLoading } = useFirestore(CARRIERS_COLLECTION, 'codigo');

    // Flag to control when to load parameter collections (only when modal opens)
    const [shouldLoadParams, setShouldLoadParams] = useState(false);

    // Load address types when modal opens
    const { data: addressTypes, loading: addressTypesLoading } = useSubCollection(
        shouldLoadParams ? ADDRESS_TYPE_COLLECTION_PATH.parentCollection : null,
        shouldLoadParams ? ADDRESS_TYPE_COLLECTION_PATH.parentId : null,
        shouldLoadParams ? ADDRESS_TYPE_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    // Get unique UF list from carriers for filter
    const uniqueUFs = useMemo(() => {
        if (!carriers) return [];
        const ufs = new Set();
        carriers.forEach(carrier => {
            if (carrier.uf) ufs.add(carrier.uf);
        });
        return Array.from(ufs).sort();
    }, [carriers]);

    // Get unique city list from carriers for filter
    const uniqueCities = useMemo(() => {
        if (!carriers) return [];
        const cities = new Set();
        carriers.forEach(carrier => {
            if (carrier.cidade) cities.add(carrier.cidade);
        });
        return Array.from(cities).sort();
    }, [carriers]);

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    // Load parameters when modal opens
    useEffect(() => {
        if (isModalOpen && !shouldLoadParams) {
            setShouldLoadParams(true);
        }
    }, [isModalOpen, shouldLoadParams]);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Get address type name
    const getAddressTypeName = (tipoEndereco) => {
        if (!tipoEndereco) return '-';
        const addressType = addressTypes?.find(t => t.name === tipoEndereco || t.id === tipoEndereco);
        return addressType?.name || tipoEndereco;
    };

    // Get tipo pessoa label
    const getTipoPessoaLabel = (value) => {
        return TIPO_PESSOA_OPTIONS.find(t => t.value === value)?.label || value || '-';
    };

    // Get situacao label
    const getSituacaoLabel = (value) => {
        return SITUACAO_OPTIONS.find(t => t.value === value)?.label || value || '-';
    };

    // Format full address
    const getFullAddress = (carrier) => {
        const parts = [];
        if (carrier.logradouro) parts.push(carrier.logradouro);
        if (carrier.numero) parts.push(carrier.numero);
        if (carrier.complemento) parts.push(carrier.complemento);
        if (carrier.bairro) parts.push(carrier.bairro);
        if (carrier.cidade) parts.push(carrier.cidade);
        if (carrier.uf) parts.push(carrier.uf);
        if (carrier.cep) parts.push(`CEP: ${carrier.cep}`);
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    // Apply filters to carriers
    const filteredCarriers = useMemo(() => {
        if (!carriers) return [];

        let filtered = [...carriers];

        // Search term
        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(carrier =>
                (carrier.codigo || '').toLowerCase().includes(term) ||
                (carrier.razaoSocial || '').toLowerCase().includes(term) ||
                (carrier.nomeFantasia || '').toLowerCase().includes(term) ||
                (carrier.cnpjCpf || '').toLowerCase().includes(term) ||
                (carrier.cidade || '').toLowerCase().includes(term) ||
                (carrier.uf || '').toLowerCase().includes(term)
            );
        }

        // Filter by tipo pessoa
        if (filters.tipoPessoa) {
            filtered = filtered.filter(carrier => carrier.tipoPessoa === filters.tipoPessoa);
        }

        // Filter by situacao
        if (filters.situacao) {
            filtered = filtered.filter(carrier => carrier.situacao === filters.situacao);
        }

        // Filter by address type
        if (filters.tipoEndereco) {
            filtered = filtered.filter(carrier => carrier.tipoEndereco === filters.tipoEndereco);
        }

        // Filter by UF
        if (filters.uf) {
            filtered = filtered.filter(carrier => carrier.uf === filters.uf);
        }

        // Filter by cidade
        if (filters.cidade) {
            filtered = filtered.filter(carrier => carrier.cidade === filters.cidade);
        }

        // Filter by carga máxima range
        if (filters.minCargaMaxima) {
            const min = parseFloat(filters.minCargaMaxima);
            if (!isNaN(min)) {
                filtered = filtered.filter(carrier => {
                    const cargaMaxima = parseFloat(carrier.cargaMaxima) || 0;
                    return cargaMaxima >= min;
                });
            }
        }

        if (filters.maxCargaMaxima) {
            const max = parseFloat(filters.maxCargaMaxima);
            if (!isNaN(max)) {
                filtered = filtered.filter(carrier => {
                    const cargaMaxima = parseFloat(carrier.cargaMaxima) || 0;
                    return cargaMaxima <= max;
                });
            }
        }

        // Filter by creation date range
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(carrier => {
                const createdAt = carrier.createdAt ? new Date(carrier.createdAt) : null;
                return createdAt && createdAt >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(carrier => {
                const createdAt = carrier.createdAt ? new Date(carrier.createdAt) : null;
                return createdAt && createdAt <= toDate;
            });
        }

        // Sort by código
        filtered.sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));

        return filtered;
    }, [carriers, filters]);

    // Pagination for main list
    const totalPages = Math.ceil((filteredCarriers?.length || 0) / ITEMS_PER_PAGE);
    const currentCarriers = (filteredCarriers || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Transportadoras');

        // Title row
        worksheet.mergeCells('A1:M1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 18, bold: true, color: { argb: 'FF2C3E50' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35;

        // Generation info
        worksheet.mergeCells('A2:M2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };
        worksheet.getCell('A2').alignment = { horizontal: 'center' };
        worksheet.getRow(2).height = 22;

        worksheet.mergeCells('A3:M3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };
        worksheet.getCell('A3').alignment = { horizontal: 'center' };
        worksheet.getRow(3).height = 22;

        // Headers - row 4
        const headers = [
            'Código', 'Razão Social', 'Nome Fantasia', 'CNPJ/CPF', 'IE',
            'Tipo Pessoa', 'Situação', 'Endereço', 'Cidade/UF', 'CEP',
            'Telefone', 'Email', 'Carga Máx (kg)'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2C3E50' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(4).height = 40;

        // Set column widths
        worksheet.getColumn(1).width = 10;
        worksheet.getColumn(2).width = 35;
        worksheet.getColumn(3).width = 30;
        worksheet.getColumn(4).width = 20;
        worksheet.getColumn(5).width = 18;
        worksheet.getColumn(6).width = 15;
        worksheet.getColumn(7).width = 12;
        worksheet.getColumn(8).width = 45;
        worksheet.getColumn(9).width = 18;
        worksheet.getColumn(10).width = 15;
        worksheet.getColumn(11).width = 18;
        worksheet.getColumn(12).width = 28;
        worksheet.getColumn(13).width = 15;

        // Data rows
        items.forEach((carrier) => {
            const row = worksheet.addRow([
                carrier.codigo || '-',
                carrier.razaoSocial || '-',
                carrier.nomeFantasia || '-',
                carrier.cnpjCpf || '-',
                carrier.inscricaoEstadual || '-',
                getTipoPessoaLabel(carrier.tipoPessoa),
                getSituacaoLabel(carrier.situacao),
                getFullAddress(carrier),
                carrier.cidade && carrier.uf ? `${carrier.cidade}/${carrier.uf}` : (carrier.cidade || carrier.uf || '-'),
                carrier.cep || '-',
                carrier.telefone || '-',
                carrier.email || '-',
                carrier.cargaMaxima || '0'
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', wrapText: true };
                if (colNumber !== 2 && colNumber !== 3 && colNumber !== 8) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            row.height = 35;

            // Color status cell
            const statusCell = row.getCell(7);
            if (carrier.situacao === 'Ativo') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFd4edda' }
                };
            } else if (carrier.situacao === 'Inativo') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8D7DA' }
                };
            }
        });

        // Summary row
        const summaryRow = worksheet.addRow([`Total de transportadoras: ${items.length}`, '', '', '', '', '', '', '', '', '', '', '', '']);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:M${worksheet.rowCount}`);
        worksheet.getRow(worksheet.rowCount).height = 28;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredCarriers.length === 0) {
            addToast('Nenhuma transportadora encontrada com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const filterDisplay = {};

            if (filters.tipoPessoa) {
                filterDisplay.tipoPessoaLabel = getTipoPessoaLabel(filters.tipoPessoa);
            }
            if (filters.situacao) {
                filterDisplay.situacaoLabel = getSituacaoLabel(filters.situacao);
            }
            if (filters.tipoEndereco) {
                filterDisplay.tipoEnderecoName = getAddressTypeName(filters.tipoEndereco);
            }
            if (filters.uf) {
                filterDisplay.uf = filters.uf;
            }
            if (filters.cidade) {
                filterDisplay.cidade = filters.cidade;
            }
            if (filters.minCargaMaxima) {
                filterDisplay.minCargaMaxima = filters.minCargaMaxima;
            }
            if (filters.maxCargaMaxima) {
                filterDisplay.maxCargaMaxima = filters.maxCargaMaxima;
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }

            let reportTitle = 'RELATÓRIO DE TRANSPORTADORAS';
            if (filterDisplay.tipoPessoaLabel && filterDisplay.tipoPessoaLabel !== 'Todos') {
                reportTitle += ` - ${filterDisplay.tipoPessoaLabel}`;
            }
            if (filterDisplay.situacaoLabel && filterDisplay.situacaoLabel !== 'Todos') {
                reportTitle += ` - ${filterDisplay.situacaoLabel}`;
            }

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.tipoPessoaLabel && filterDisplay.tipoPessoaLabel !== 'Todos') filterParts.push(filterDisplay.tipoPessoaLabel.replace(/\s/g, '_'));
            if (filterDisplay.situacaoLabel && filterDisplay.situacaoLabel !== 'Todos') filterParts.push(filterDisplay.situacaoLabel.replace(/\s/g, '_'));
            if (filterDisplay.uf) filterParts.push(filterDisplay.uf);
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Transportadoras${filterSuffix}_${timestamp}.xlsx`;

            await generateExcelReport(filteredCarriers, filterDisplay, reportTitle, fileName);

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                carrierCount: filteredCarriers.length,
                filters: {
                    searchTerm: filters.searchTerm || null,
                    tipoPessoa: filters.tipoPessoa,
                    tipoPessoaLabel: filterDisplay.tipoPessoaLabel || null,
                    situacao: filters.situacao,
                    situacaoLabel: filterDisplay.situacaoLabel || null,
                    tipoEndereco: filters.tipoEndereco,
                    tipoEnderecoName: filterDisplay.tipoEnderecoName || null,
                    uf: filters.uf || null,
                    cidade: filters.cidade || null,
                    minCargaMaxima: filters.minCargaMaxima || null,
                    maxCargaMaxima: filters.maxCargaMaxima || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            searchTerm: '',
            tipoPessoa: '',
            situacao: '',
            tipoEndereco: '',
            uf: '',
            cidade: '',
            minCargaMaxima: '',
            maxCargaMaxima: '',
            dateFrom: '',
            dateTo: ''
        });
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filtersObj) => {
        if (!filtersObj) return '-';
        const parts = [];
        if (filtersObj.tipoPessoaLabel && filtersObj.tipoPessoaLabel !== 'Todos') parts.push(`Tipo: ${filtersObj.tipoPessoaLabel}`);
        if (filtersObj.situacaoLabel && filtersObj.situacaoLabel !== 'Todos') parts.push(`Status: ${filtersObj.situacaoLabel}`);
        if (filtersObj.tipoEnderecoName) parts.push(`Tipo End.: ${filtersObj.tipoEnderecoName}`);
        if (filtersObj.uf) parts.push(`UF: ${filtersObj.uf}`);
        if (filtersObj.cidade) parts.push(`Cidade: ${filtersObj.cidade}`);
        if (filtersObj.minCargaMaxima) parts.push(`Carga Mín: ${filtersObj.minCargaMaxima}kg`);
        if (filtersObj.maxCargaMaxima) parts.push(`Carga Máx: ${filtersObj.maxCargaMaxima}kg`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) parts.push(`Busca: ${filtersObj.searchTerm}`);
        if (parts.length === 0) return 'Todas as transportadoras';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = carriersLoading;

    // Check if any filter is active
    const hasActiveFilters = filters.tipoPessoa || filters.situacao || filters.tipoEndereco ||
        filters.uf || filters.cidade || filters.minCargaMaxima || filters.maxCargaMaxima ||
        filters.dateFrom || filters.dateTo || filters.searchTerm;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {addressTypesLoading && (
                <div className={styles.infoBox}>
                    <span>🔄</span>
                    <span>Carregando parâmetros do sistema...</span>
                </div>
            )}

            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por Código, Razão Social, Fantasia, CNPJ, Cidade ou UF</label>
                <input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Basic Filters Row */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Tipo de Pessoa</label>
                    <select
                        value={filters.tipoPessoa}
                        onChange={(e) => handleFilterChange('tipoPessoa', e.target.value)}
                        className={styles.select}
                    >
                        {TIPO_PESSOA_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Situação</label>
                    <select
                        value={filters.situacao}
                        onChange={(e) => handleFilterChange('situacao', e.target.value)}
                        className={styles.select}
                    >
                        {SITUACAO_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Tipo de Endereço</label>
                    <select
                        value={filters.tipoEndereco}
                        onChange={(e) => handleFilterChange('tipoEndereco', e.target.value)}
                        className={styles.select}
                        disabled={addressTypesLoading}
                    >
                        <option value="">Todos os tipos</option>
                        {addressTypes?.map(type => (
                            <option key={type.id} value={type.name}>{type.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Location Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>UF (Estado)</label>
                    <select
                        value={filters.uf}
                        onChange={(e) => handleFilterChange('uf', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todos os estados</option>
                        {uniqueUFs.map(uf => (
                            <option key={uf} value={uf}>{uf}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Cidade</label>
                    <select
                        value={filters.cidade}
                        onChange={(e) => handleFilterChange('cidade', e.target.value)}
                        className={styles.select}
                        disabled={!filters.uf && uniqueCities.length > 50}
                    >
                        <option value="">Todas as cidades</option>
                        {uniqueCities.map(cidade => (
                            <option key={cidade} value={cidade}>{cidade}</option>
                        ))}
                    </select>
                    <small className={styles.fieldHint}>
                        {!filters.uf && uniqueCities.length > 50 && 'Selecione um estado para filtrar cidades'}
                    </small>
                </div>
            </div>

            {/* Carga Máxima Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Carga Máxima Mínima (kg)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 500"
                        value={filters.minCargaMaxima}
                        onChange={(e) => handleFilterChange('minCargaMaxima', e.target.value)}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label>Carga Máxima Máxima (kg)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 10000"
                        value={filters.maxCargaMaxima}
                        onChange={(e) => handleFilterChange('maxCargaMaxima', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Preview Info */}
            <div className={styles.previewInfo}>
                📊 {filteredCarriers.length} {filteredCarriers.length === 1 ? 'transportadora encontrada' : 'transportadoras encontradas'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Transportadoras"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredCarriers.length})`}
                cancelText="Cancelar"
                maxWidth="860px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Transportadoras"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.searchTerm && (
                        <span className={styles.filterBadge}>
                            🔍 Busca: {filters.searchTerm}
                        </span>
                    )}
                    {filters.tipoPessoa && (
                        <span className={styles.filterBadge}>
                            📋 Tipo: {getTipoPessoaLabel(filters.tipoPessoa)}
                        </span>
                    )}
                    {filters.situacao && (
                        <span className={styles.filterBadge}>
                            🟢 Status: {getSituacaoLabel(filters.situacao)}
                        </span>
                    )}
                    {filters.tipoEndereco && (
                        <span className={styles.filterBadge}>
                            🏷️ Tipo End.: {getAddressTypeName(filters.tipoEndereco)}
                        </span>
                    )}
                    {filters.uf && (
                        <span className={styles.filterBadge}>
                            📍 UF: {filters.uf}
                        </span>
                    )}
                    {filters.cidade && (
                        <span className={styles.filterBadge}>
                            🏙️ Cidade: {filters.cidade}
                        </span>
                    )}
                    {filters.minCargaMaxima && (
                        <span className={styles.filterBadge}>
                            ⚖️ Carga Mín: {filters.minCargaMaxima}kg
                        </span>
                    )}
                    {filters.maxCargaMaxima && (
                        <span className={styles.filterBadge}>
                            ⚖️ Carga Máx: {filters.maxCargaMaxima}kg
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>
            )}

            {/* Report History Section */}
            <div className={`${styles.tableContainer} ${styles.historyTable}`}>
                <h3 className={styles.tableTitle}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th>Gerado por</th>
                                <th>Transportadoras</th>
                                <th>Filtros Aplicados</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto 8px auto' }}></div>
                                        <span>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span className={styles.fileName}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.userInfo}>
                                                <span className={styles.userName}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span className={styles.userEmail}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                            {report.carrierCount}
                                        </td>
                                        <td className={styles.filterDescription}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton} ${styles.smallDeleteButton}`}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CarrierListReportPage;